import { berlinDatum } from "@dj/domain/gemeinsam";
import { collection, doc, onSnapshot, query, where, type DocumentData, type Unsubscribe } from "firebase/firestore";
import { useSyncExternalStore } from "react";
import { firebaseDienste, firestoreBeobachten, firestorePersistent, firestoreUmstellen } from "./firebase";
import { lesemodelleVergessen } from "./lesemodelle";
import { serverJetzt, serverkontakt } from "./serverzeit";

/**
 * Daten der Drückjagd auf dem Gerät nur am Jagdtag (ADR-0020, Anforderungen N-41, N-42, §7a K31, K60; Vertrag
 * oberflaeche.md §10).
 *
 * - **Laden (K60):** Öffnet eine angemeldete Person die App am Jagdtag (Serverzeit, Europe/Berlin) mit Netz, startet
 *   Firestore mit persistentem Cache und hält für jede Drückjagd dieses Tages, an der sie mitwirkt, Listener auf alle
 *   Lesemodelle, die ihre Ansichten ohne Netz zeigen (lesemodelle.md §3–§5), ohne weiteren Fingertipp. Hat jeder davon
 *   einmal vom Server geliefert (`fromCache === false`, damit im persistenten Cache), gilt die Drückjagd als „Offline
 *   bereit“. Welche Daten die Person sieht, entscheiden allein die Leserlisten (ADR-0010): mehr als ihre Sicht kommt nie
 *   auf das Gerät (LIM-0007).
 * - **Löschen (K31):** Das geöffnete Fenster prüft jede Sekunde nach Serverzeit (Geräteuhr plus zuletzt bekannte
 *   Abweichung, ADR-0030), ob der gemerkte Jagdtag vorbei ist; beim nächsten Start geschieht es vor Firebase. Ebenso
 *   gelöscht wird, wenn eine Drückjagd des Tages nicht mehr lesbar ist (Zugang entzogen, N-40).
 * - **Speicher geleert (ADR-0020 §Consequences):** Fehlt der gemerkte Jagdtag im `localStorage`, obwohl das Fenster ihn
 *   kennt, hat der Browser den Speicher geräumt: mit Netz lädt die App neu, ohne Netz zeigt sie den Hinweis.
 *
 * Im `localStorage` stehen nur Datum und Kennungen, nie Inhalte. Der Service Worker entscheidet nichts nach der Uhr.
 */
const JAGDTAG = "dj-jagdtag";
const BEREIT = "dj-offline-bereit";
const TAKT_MS = 1000;

export interface Offlinestand {
  /** Drückjagden, deren Offline-Daten vollständig auf dem Gerät liegen */
  bereit: ReadonlySet<string>;
  /** Am Jagdtag fehlen die Daten (Speicher geleert) und das Gerät hat kein Netz */
  fehlen: boolean;
  /** Firestore hält heute den persistenten Cache des Jagdtags */
  jagdtag: boolean;
}

let stand: Offlinestand = { bereit: new Set(), fehlen: false, jagdtag: false };
const hoerer = new Set<() => void>();

function setzen(neu: Partial<Offlinestand>): void {
  stand = { ...stand, ...neu };
  for (const h of hoerer) h();
}

export function useOfflinestand(): Offlinestand {
  return useSyncExternalStore((f) => { hoerer.add(f); return () => hoerer.delete(f); }, () => stand);
}

/** Heutiges Datum nach Serverzeit in Europe/Berlin. */
export function heute(): string {
  return berlinDatum(serverJetzt());
}

function bereitGemerkt(): Set<string> {
  try {
    const b = JSON.parse(localStorage.getItem(BEREIT) ?? "null") as { datum?: string; drueckjagden?: string[] } | null;
    return b?.datum === heute() ? new Set(b.drueckjagden ?? []) : new Set();
  } catch {
    return new Set();
  }
}

function bereitMerken(ids: ReadonlySet<string>): void {
  localStorage.setItem(BEREIT, JSON.stringify({ datum: heute(), drueckjagden: [...ids] }));
}

async function cacheDatenbankenLoeschen(): Promise<void> {
  const namen = (await indexedDB.databases?.() ?? []).map((d) => d.name ?? "").filter((n) => n.startsWith("firestore/"));
  await Promise.all(namen.map((n) => new Promise<void>((fertig) => {
    const r = indexedDB.deleteDatabase(n);
    r.onsuccess = r.onerror = r.onblocked = () => fertig();
  })));
}

/**
 * Beim Start vor Firebase: Liegt der gemerkte Jagdtag in der Vergangenheit (oder Zukunft), werden die Offline-Daten
 * gelöscht, bevor irgendetwas sie lesen kann. Ergebnis: Firestore startet mit persistentem Cache (heute ist Jagdtag).
 */
export async function offlineVorbereiten(): Promise<boolean> {
  const tag = localStorage.getItem(JAGDTAG);
  if (tag === heute()) {
    setzen({ jagdtag: true, bereit: bereitGemerkt() });
    return true;
  }
  if (tag) {
    localStorage.removeItem(JAGDTAG);
    localStorage.removeItem(BEREIT);
    await cacheDatenbankenLoeschen();
  }
  return false;
}

// ---------------------------------------------------------------------------------------------------------------------
// Begleitung einer Sitzung
// ---------------------------------------------------------------------------------------------------------------------

interface DrueckjagdDesTages {
  id: string;
  revier: string | null;
}

interface Quelle {
  art: "dokument" | "sammlung";
  pfad: string;
}

/** Lesemodelle, die die Ansichten einer Drückjagd ohne Netz zeigen (lesemodelle.md §3, §4, §5; Vertrag §3.2, §6). */
function quellen(uid: string, dj: DrueckjagdDesTages): Quelle[] {
  const d = (pfad: string): Quelle => ({ art: "dokument", pfad });
  const s = (pfad: string): Quelle => ({ art: "sammlung", pfad });
  const p = `drueckjagden/${dj.id}`;
  const liste: Quelle[] = [
    d(`${p}/kopf/${uid}`), d(`${p}/meine_aufgaben/${uid}`), d(`${p}/jagdzustand/aktuell`),
    s(`${p}/meine_aufgaben/${uid}/zugewiesene_suchen`), s(`personen/${uid}/eigene_meldungen`),
    ...["tagesablauf", "sicherheitsbestimmungen", "hinweise", "notfallkontakte", "rettungspunkte", "hunde_im_treiben",
      "offene_notrufe", "vermisste_hunde"].map((t) => s(`${p}/${t}`)),
  ];
  if (dj.revier) {
    const r = `${p}/reviere/${dj.revier}`;
    liste.push(d(`${r}/einweisung/aktuell`),
      ...["standbesetzung", "freigabe", "kontingentstand", "abholliste", "bergeliste", "offene_suchen", "kontrollliste",
        "lagebild_schuesse", "lagebild_anstellung", "lagebild_nicht_abgeholt", "lagebild_treibergruppen", "lagebild_offene_suchen",
        "lagebild_unkontrolliert", "anstellgruppen", "treibergruppen"].map((t) => s(`${r}/${t}`)));
  }
  return liste;
}

let uidAktiv: string | null = null;
let meine: { zeilen: DrueckjagdDesTages[]; jagddaten: Map<string, string>; vomServer: boolean } = { zeilen: [], jagddaten: new Map(), vomServer: false };
let meineAbmelden: Unsubscribe | null = null;
/** Jagdtag, den dieses Fenster gemerkt hat (übersteht das Räumen des Speichers durch den Browser) */
let gemerktImFenster: string | null = null;
let arbeitet = false;
/** Drückjagden des heutigen Jagdtags (zuletzt vom Server bestätigt) */
let heutigeIds: string[] = [];

interface Laden {
  abmelden: Unsubscribe[];
  offen: Set<string>;
  kanaeleVomServer: boolean;
}
const laden = new Map<string, Laden>();

function ladenBeenden(): void {
  for (const l of laden.values()) for (const a of l.abmelden) a();
  laden.clear();
}

function bereitPruefen(id: string): void {
  const l = laden.get(id);
  if (!l || l.offen.size || !l.kanaeleVomServer || stand.bereit.has(id)) return;
  const bereit = new Set(stand.bereit).add(id);
  bereitMerken(bereit);
  setzen({ bereit, fehlen: stand.fehlen && !heutigeIds.every((h) => bereit.has(h)) });
}

/** Ende des letzten Jagdabschnitts je Drückjagd des Tages (`Jagdzeitplan` des Jagdzustands), `null` ohne Jagdzeitplan */
const jagdende = new Map<string, Date | null>();

function jagdendeMerken(id: string, daten: DocumentData | undefined): void {
  const plan = (daten?.["Jagdzeitplan"] as Array<{ Ende?: { toDate?: () => Date } }> | undefined) ?? [];
  const enden = plan.map((a) => a.Ende?.toDate?.()).filter((d): d is Date => d instanceof Date && !Number.isNaN(d.getTime()));
  jagdende.set(id, enden.length ? new Date(Math.max(...enden.map((d) => d.getTime()))) : null);
}

/**
 * Läuft auf diesem Gerät ein Jagdtag (ADR-0041 §4)? Ja, sobald eine Drückjagd „Offline bereit“ ist, bis zum Ende ihres
 * letzten Jagdabschnitts nach Serverzeit; ohne bekannten Jagdzeitplan bis zum Ende des Jagdtags (dann löscht die App die
 * Offline-Daten, K31).
 */
export function jagdtagLaeuft(): boolean {
  const jetzt = serverJetzt();
  for (const id of stand.bereit) {
    const ende = jagdende.get(id);
    if (!ende || jetzt < ende) return true;
  }
  return false;
}

function beobachten(id: string, l: Laden, q: Quelle, beiDaten?: (docs: Array<{ id: string; daten: DocumentData }>) => void): void {
  const { db } = firebaseDienste();
  l.offen.add(q.pfad);
  const erledigt = () => { if (l.offen.delete(q.pfad)) bereitPruefen(id); };
  const fehler = () => erledigt();
  if (q.art === "dokument") {
    l.abmelden.push(onSnapshot(doc(db, q.pfad), { includeMetadataChanges: true }, (snap) => {
      if (q.pfad.endsWith("/jagdzustand/aktuell")) jagdendeMerken(id, snap.data());
      if (!snap.metadata.fromCache) erledigt();
    }, fehler));
  } else {
    l.abmelden.push(onSnapshot(query(collection(db, q.pfad), where("_leser", "array-contains", uidAktiv)), { includeMetadataChanges: true }, (snap) => {
      beiDaten?.(snap.docs.map((x) => ({ id: x.id, daten: x.data() })));
      if (!snap.metadata.fromCache) erledigt();
    }, fehler));
  }
}

function ladenStarten(uid: string, dj: DrueckjagdDesTages): void {
  const l: Laden = { abmelden: [], offen: new Set(), kanaeleVomServer: false };
  laden.set(dj.id, l);
  for (const q of quellen(uid, dj)) beobachten(dj.id, l, q);
  // Kanaltexte der Drückjagd (K17): die Kanäle der Person, je Kanal dieser Drückjagd der Verlauf.
  const verlaeufe = new Set<string>();
  const { db } = firebaseDienste();
  l.abmelden.push(onSnapshot(query(collection(db, "kanaele"), where("_leser", "array-contains", uid)), { includeMetadataChanges: true }, (snap) => {
    for (const k of snap.docs) {
      if (k.data()["_drueckjagd"] !== dj.id || verlaeufe.has(k.id)) continue;
      verlaeufe.add(k.id);
      beobachten(dj.id, l, { art: "sammlung", pfad: `kanaele/${k.id}/nachrichten` });
    }
    if (!snap.metadata.fromCache) { l.kanaeleVomServer = true; bereitPruefen(dj.id); }
  }, () => { l.kanaeleVomServer = true; bereitPruefen(dj.id); }));
}

function meineBeobachten(uid: string): void {
  meineAbmelden?.();
  meine = { zeilen: [], jagddaten: new Map(), vomServer: false };
  const { db } = firebaseDienste();
  meineAbmelden = onSnapshot(query(collection(db, `personen/${uid}/meine_drueckjagden`), where("_leser", "array-contains", uid)),
    { includeMetadataChanges: true }, (snap) => {
      const jagddaten = new Map<string, string>();
      const zeilen = snap.docs.map((x) => {
        const d = x.data();
        jagddaten.set(x.id, String(d["Jagddatum"] ?? ""));
        return { id: x.id, revier: ((d["_kennungen"] as Record<string, string> | undefined)?.["Revier"]) ?? null };
      });
      meine = { zeilen, jagddaten, vomServer: !snap.metadata.fromCache };
      void schritt();
    }, (e) => {
      meine = { ...meine, vomServer: false };
      // Ohne Leserecht auf die eigenen Drückjagden (Sitzung beendet, Zugang entzogen) bleibt nichts auf dem Gerät (N-40).
      if (e.code === "permission-denied" && navigator.onLine && localStorage.getItem(JAGDTAG)) void loeschen(null);
    });
}

/** Löscht die Offline-Daten (Firestore-Cache) und startet Firestore neu, mit Cache nur, wenn `weiter` heute Jagdtag ist. */
async function loeschen(weiter: string | null): Promise<void> {
  ladenBeenden();
  localStorage.removeItem(BEREIT);
  if (weiter) localStorage.setItem(JAGDTAG, weiter);
  else localStorage.removeItem(JAGDTAG);
  gemerktImFenster = weiter;
  setzen({ bereit: new Set(), jagdtag: !!weiter, fehlen: false });
  lesemodelleVergessen();
  await firestoreUmstellen(!!weiter, true);
}

async function schritt(): Promise<void> {
  if (arbeitet || !uidAktiv) return;
  arbeitet = true;
  try {
    const uid = uidAktiv;
    const tag = heute();
    const gemerkt = localStorage.getItem(JAGDTAG);
    const online = navigator.onLine;
    // Ende des Jagdtags (K31), mit und ohne Netz.
    if ((gemerkt && gemerkt !== tag) || (gemerktImFenster && gemerktImFenster !== tag)) {
      await loeschen(null);
      return;
    }
    // Speicher vom Browser geleert (ADR-0020 §Consequences): Firestore hat sich dabei selbst beendet. Neu starten; die
    // Daten fehlen, bis jede Drückjagd des Tages wieder „Offline bereit“ ist (mit Netz lädt die App sie neu).
    if (gemerktImFenster === tag && !gemerkt) {
      localStorage.setItem(JAGDTAG, tag);
      ladenBeenden();
      setzen({ bereit: new Set(), fehlen: true });
      await firestoreUmstellen(true, false);
      return;
    }
    if (!online || !meine.vomServer) return;
    const heutige = meine.zeilen.filter((z) => meine.jagddaten.get(z.id) === tag);
    heutigeIds = heutige.map((z) => z.id);
    // Drückjagd des Tages nicht mehr lesbar (Zugang entzogen, abgesagt): Daten weg, Rest neu laden.
    const weg = [...new Set([...stand.bereit, ...laden.keys()])].filter((id) => !heutige.some((z) => z.id === id));
    if (weg.length && firestorePersistent()) {
      await loeschen(heutige.length ? tag : null);
      return;
    }
    if (!heutige.length) return;
    // Beginn des Jagdtags (K60): Firestore mit Cache, dann laden — erst nach einem Abgleich mit der Serverzeit.
    if (!firestorePersistent()) {
      if (!serverkontakt().letzterAbgleich) return;
      localStorage.setItem(JAGDTAG, tag);
      gemerktImFenster = tag;
      setzen({ jagdtag: true });
      await firestoreUmstellen(true, false);
      return;
    }
    if (!gemerktImFenster) gemerktImFenster = tag;
    for (const dj of heutige) if (!laden.has(dj.id)) ladenStarten(uid, dj);
  } finally {
    arbeitet = false;
  }
}

/** Nach dem Abmelden (oder Ende der Sitzung) bleiben keine Offline-Daten auf dem Gerät (N-40). */
export async function offlineAbmelden(): Promise<void> {
  if (localStorage.getItem(JAGDTAG) || firestorePersistent()) await loeschen(null);
}

/** Begleitet die angemeldete Sitzung; gibt eine Abmeldefunktion zurück. */
export function jagdtagBegleiten(uid: string): () => void {
  uidAktiv = uid;
  if (firestorePersistent()) gemerktImFenster = localStorage.getItem(JAGDTAG);
  meineBeobachten(uid);
  const neu = firestoreBeobachten(() => {
    ladenBeenden();
    if (uidAktiv) meineBeobachten(uidAktiv);
  });
  const takt = window.setInterval(() => void schritt(), TAKT_MS);
  const online = () => void schritt();
  window.addEventListener("online", online);
  window.addEventListener("offline", online);
  return () => {
    window.clearInterval(takt);
    window.removeEventListener("online", online);
    window.removeEventListener("offline", online);
    neu();
    meineAbmelden?.();
    meineAbmelden = null;
    ladenBeenden();
    uidAktiv = null;
  };
}
